"use client";

import { FetchTooltip } from "@/fetch-ui/FetchTooltip";
import { DEFAULT_PRIMARY_RGB, hexToRgb } from "@/lib/color/hex";
import { useEnterpriseTheme } from "@/lib/enterprise/EnterpriseProvider";
import Image from "next/image";
import { useState } from "react";
import { createPortal } from "react-dom";

interface FetchHighlightedTextProps {
  children: string;
  imageSrc: string;
  imageAlt: string;
  cursorIconSrc?: string;
}

/**
 * Nowadays themed highlighted text component.
 * Shows a light primary-colored highlight that darkens on hover, displays an image tooltip,
 * and replaces the cursor with a custom icon (defaults to the confused Nowadays icon).
 *
 * @param children - The text to highlight.
 * @param imageSrc - Image shown in the tooltip while hovering.
 * @param imageAlt - Alt text for that image.
 * @param cursorIconSrc - Icon that replaces the cursor over the highlight.
 * @returns The highlighted text and its hover tooltip.
 */
export const FetchHighlightedText = ({
  children,
  imageSrc,
  imageAlt,
  cursorIconSrc = "/nowadays_icons/nowadays-confused.png",
}: FetchHighlightedTextProps) => {
  // States
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  // Get primary color and make it lighter for highlighting
  const { primaryColor } = useEnterpriseTheme();
  const primaryRgb = hexToRgb(primaryColor) ?? DEFAULT_PRIMARY_RGB;
  const lightHighlight = `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.15)`;
  const hoverHighlight = `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.25)`;

  // Handlers
  const handleMouseMove = (e: React.MouseEvent) => {
    setCursorPosition({ x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <FetchTooltip
        content={
          <div className="p-2">
            <Image src={imageSrc} alt={imageAlt} width={200} height={200} className="rounded-md" />
          </div>
        }
        side="top"
        align="center"
        delayDuration={200}
        className="bg-white border border-gray-200 shadow-lg p-0"
      >
        <span
          className="transition-all duration-200 rounded px-1 py-0.5"
          style={{
            backgroundColor: lightHighlight,
            cursor: isHovering ? "none" : "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = hoverHighlight;
            setIsHovering(true);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = lightHighlight;
            setIsHovering(false);
          }}
          onMouseMove={handleMouseMove}
        >
          {children}
        </span>
      </FetchTooltip>

      {/* Custom cursor icon — portaled to document.body since this component can be used inside
          a <p>, and a <div> here isn't a valid child of <p> */}
      {isHovering &&
        cursorIconSrc &&
        createPortal(
          <div
            className="fixed pointer-events-none z-10001"
            style={{
              left: `${cursorPosition.x}px`,
              top: `${cursorPosition.y}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <Image src={cursorIconSrc} alt="" width={24} height={24} />
          </div>,
          document.body
        )}
    </>
  );
};
